/* app.jsx — App shell, sidebar, routing, theme
 * Design: v6 (new sidebar, workspace chip, nav groups, quick chips, mobile)
 * Logic:  v5 (useAuth, RLS role-gating, hash routing, preview mode)
 */

import React from 'react'
import { I } from './icons.jsx'
import { humanize } from './utils.jsx'
import { selectAll } from './db.jsx'
import { useAuth, signOut, useInactivityLogout, LoginScreen, ResetScreen, WelcomeScreen } from './auth.jsx'
import { Spinner, NavItem, ToastHost, Modal } from './components.jsx'
import { ErrorBar } from './errors.jsx'
import { Dashboard } from './views/Dashboard.jsx'
import { OrdersView } from './views/Orders.jsx'
import { ContactsView } from './views/Contacts.jsx'
import { RidersView } from './views/Riders.jsx'
import { ProductionView, InventoryView, ProductsView, ExpensesView, UsersView } from './views/SimpleViews.jsx'
import { ChatAIView } from './views/ChatAI.jsx'

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-inner">
        <img src="uploads/baguio-01.png" alt="Loading" className="loading-logo" />
        <span className="loading-label">Loading…</span>
      </div>
    </div>
  );
}

// Capture URL at module load — before Supabase cleans up the invite/reset tokens
const INIT_SEARCH = location.search;
const INIT_HASH   = location.hash;

const ROUTES = [
  { id: "home",       label: "Home",        icon: null, View: ({ goto }) => <Dashboard goto={goto}/> },
  { id: "orders",     label: "Orders",      icon: null, View: ({ goto }) => <OrdersView goto={goto}/> },
  { id: "production", label: "Production",  icon: null, View: () => <ProductionView/> },
  { id: "inventory",  label: "Inventory",   icon: null, View: () => <InventoryView/> },
  { id: "products",   label: "Products",    icon: null, View: () => <ProductsView/> },
  { id: "contacts",   label: "Contacts",    icon: null, View: () => <ContactsView/> },
  { id: "riders",     label: "Riders",      icon: null, View: () => <RidersView/> },
  { id: "expenses",   label: "Expenses",    icon: null, View: () => <ExpensesView/> },
  { id: "users",      label: "User mgmt",   icon: null, adminOnly: true, View: () => <UsersView/> },
  { id: "chat",       label: "Smart Log",    icon: null, View: () => <ChatAIView/> },
];

// Attach icons after I is defined (icons.jsx loads before app.jsx)
ROUTES[0].icon = I.home;
ROUTES[1].icon = I.orders;
ROUTES[2].icon = I.production;
ROUTES[3].icon = I.inventory;
ROUTES[4].icon = I.products;
ROUTES[5].icon = I.contacts;
ROUTES[6].icon = I.riders;
ROUTES[7].icon = I.expenses;
ROUTES[8].icon = I.users;
ROUTES[9].icon = I.spark;

const HEADINGS = {
  home:       { title: "Home",       sub: "Here's what's happening with What's Your Flan today." },
  orders:     { title: "Orders",     sub: "Active customer orders across all delivery methods." },
  production: { title: "Production", sub: "Batches in flight, linked to orders or made-to-stock." },
  inventory:  { title: "Inventory",  sub: "Live stock levels, waste log, low-stock alerts." },
  products:   { title: "Products",   sub: "Flan-based dessert tins, their recipes and pricing." },
  contacts:   { title: "Contacts",   sub: "Customers, resellers, wholesale accounts." },
  riders:     { title: "Riders",     sub: "Delivery team and active assignments." },
  expenses:   { title: "Expenses",   sub: "All business spend, used for net profit." },
  users:      { title: "User management", sub: "Team members and their roles." },
  chat:       { title: "Smart Log",        sub: "Snap a receipt or type a batch note — AI logs it for you." },
};

function useTheme() {
  const [theme, setTheme] = React.useState(() =>
    localStorage.getItem("wyf-theme") ||
    (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
  );
  React.useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("wyf-theme", theme);
  }, [theme]);
  return [theme, setTheme];
}

function useHashRoute(defaultRoute) {
  const [route, setRoute] = React.useState(() => (location.hash || "#" + defaultRoute).slice(1) || defaultRoute);
  React.useEffect(() => {
    const onHash = () => setRoute((location.hash || "#" + defaultRoute).slice(1) || defaultRoute);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [defaultRoute]);
  return route;
}

function useMedia(query) {
  const [match, setMatch] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatch(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return match;
}

/* ===== Sidebar nav search ===== */
function SidebarSearch({ onNavigate }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    setBusy(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      const lq = q.trim().toLowerCase();
      const [contacts, orders, products, riders] = await Promise.all([
        selectAll("contacts_with_stats", { order: { column: "name" } }),
        selectAll("orders_list", { order: { column: "created_at", ascending: false }, limit: 200 }),
        selectAll("products_with_pricing", { order: { column: "name" } }),
        selectAll("riders_list", { order: { column: "name" } }),
      ]);
      if (cancelled) return;
      setResults({
        Contacts: (contacts.data || []).filter(r => (r.name || "").toLowerCase().includes(lq)).slice(0, 4).map(r => ({ label: r.name, sub: humanize(r.type || ""), id: r.id, route: "contacts" })),
        Orders:   (orders.data   || []).filter(r => String(r.order_number || "").toLowerCase().includes(lq) || (r.contact_name || "").toLowerCase().includes(lq)).slice(0, 4).map(r => ({ label: String(r.order_number || r.id?.slice(0,8)), sub: r.contact_name || "", id: r.id, route: "orders" })),
        Products: (products.data || []).filter(r => (r.name || "").toLowerCase().includes(lq)).slice(0, 4).map(r => ({ label: r.name, sub: r.category || "", id: r.id, route: "products" })),
        Riders:   (riders.data   || []).filter(r => (r.name || "").toLowerCase().includes(lq)).slice(0, 4).map(r => ({ label: r.name, sub: humanize(r.service_type || ""), id: r.id, route: "riders" })),
      });
      setBusy(false);
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  // Close on Escape or click outside
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setQ(""); setResults(null); } };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) { setResults(null); } };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, []);

  const hasAny = results && Object.values(results).some(a => a.length > 0);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div className="searchbar">
        <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{busy ? <span style={{ fontSize: 11 }}>…</span> : I.search}</span>
        <input
          placeholder="Search"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        {q && <button onClick={() => { setQ(""); setResults(null); }} style={{ background: "none", border: 0, color: "var(--text-faint)", cursor: "pointer", fontSize: 13, padding: "0 2px" }}>×</button>}
        {!q && <span className="kbd">⌘K</span>}
      </div>
      {results && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "var(--surface-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, zIndex: 200, boxShadow: "0 16px 40px rgba(0,0,0,.5)", overflow: "hidden", maxHeight: 360, overflowY: "auto" }}>
          {!hasAny
            ? <div style={{ padding: "12px 14px", color: "var(--text-faint)", fontSize: 13 }}>No results for "{q}"</div>
            : Object.entries(results).map(([cat, items]) => items.length === 0 ? null : (
              <div key={cat}>
                <div style={{ padding: "8px 14px 4px", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--text-faint)", fontWeight: 600 }}>{cat}</div>
                {items.map(item => (
                  <button key={item.id} onClick={() => { onNavigate(item.route); setQ(""); setResults(null); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "none", border: 0, cursor: "pointer", textAlign: "left", color: "var(--text)" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface-3)"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</div>
                      {item.sub && <div style={{ fontSize: 11, color: "var(--text-faint)" }}>{item.sub}</div>}
                    </div>
                  </button>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/* ===== Sidebar ===== */
function Sidebar({ current, onNavigate, profile, onSignOut, open, mobile, theme, onToggleTheme }) {
  const role = profile ? profile.role : "viewer";
  const initials = profile
    ? (profile.full_name || "").slice(0, 2).toUpperCase() || "??"
    : "??";
  const visibleRoutes = ROUTES.filter(r => !r.adminOnly || role === "admin");

  const className = "sidebar" + (mobile && open ? " open" : "");
  return (
    <aside className={className} style={mobile && !open ? { display: "none" } : undefined}>
      <div className="workspace">
        <div className="logo-chip"><img src="assets/logo.png" alt="WYF"/></div>
        <div className="ws-meta">
          <div className="ws-name">What's Your Flan?</div>
          <div className="ws-plan">Baguio · Demo</div>
        </div>
        <span className="ws-caret">{I.caret}</span>
      </div>

      <SidebarSearch onNavigate={onNavigate} />

      <div className="nav-group">Main menu</div>
      {visibleRoutes.slice(0, 1).map(r => (
        <NavItem key={r.id} {...r} active={r.id === current} onClick={() => onNavigate(r.id)}/>
      ))}

      <div className="nav-group">Operations</div>
      {visibleRoutes.filter(r => ["orders","production","inventory","products"].includes(r.id)).map(r => (
        <NavItem key={r.id} {...r} active={r.id === current} onClick={() => onNavigate(r.id)}/>
      ))}

      <div className="nav-group">Directory</div>
      {visibleRoutes.filter(r => ["contacts","riders","expenses"].includes(r.id)).map(r => (
        <NavItem key={r.id} {...r} active={r.id === current} onClick={() => onNavigate(r.id)}/>
      ))}

      <div className="nav-group">Tools</div>
      {visibleRoutes.filter(r => ["chat"].includes(r.id)).map(r => (
        <NavItem key={r.id} {...r} active={r.id === current} onClick={() => onNavigate(r.id)}/>
      ))}

      {role === "admin" && (
        <>
          <div className="nav-group">Admin</div>
          {visibleRoutes.filter(r => r.adminOnly).map(r => (
            <NavItem key={r.id} {...r} active={r.id === current} onClick={() => onNavigate(r.id)}/>
          ))}
        </>
      )}

      <div className="sidebar-foot">
        <div className="avatar">{initials}</div>
        <div className="who">
          <div className="name">{profile ? (profile.full_name || profile.email) : "Loading…"}</div>
          <div className="email">{profile ? humanize(profile.role) : ""}</div>
        </div>
        <button className="btn btn-ghost btn-icon" title="Sign out" onClick={onSignOut}>{I.logout}</button>
      </div>
    </aside>
  );
}

/* ===== AppShell ===== */
function AppShell({ user, profile }) {
  const [theme, setTheme] = useTheme();
  const route = useHashRoute("home");
  const inactivityWarning = useInactivityLogout(30 * 60 * 1000);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const isMobile = useMedia("(max-width: 960px)");

  const goto = (id) => { location.hash = id; setMenuOpen(false); };
  const current = ROUTES.find(r => r.id === route) || ROUTES[0];
  const heading = HEADINGS[current.id] || { title: current.label };

  // Personalise greeting on home
  const firstName = profile
    ? (profile.full_name || user.email || "").trim().split(/\s+/)[0]
    : "";
  const displayHeading = current.id === "home"
    ? { title: firstName ? `Welcome back, ${firstName}` : "Welcome back", sub: heading.sub }
    : heading;

  return (
    <ToastHost>
      {inactivityWarning && (
        <Modal title="Still there?" onClose={() => {}}>
          <p style={{ margin: 0 }}>You'll be signed out in 60 seconds due to inactivity. Click anywhere to stay signed in.</p>
        </Modal>
      )}
      <div className="app-shell">
        <Sidebar
          current={current.id}
          onNavigate={goto}
          profile={profile}
          onSignOut={signOut}
          open={!isMobile || menuOpen}
          mobile={isMobile}
          theme={theme}
          onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
        />
        {isMobile && menuOpen && (
          <div className="sidebar-mask open" onClick={() => setMenuOpen(false)} />
        )}
        <div className="main-wrap">
          <div className="topbar">
            {isMobile && (
              <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                {I.menu}
              </button>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="crumb">
                {displayHeading.title.includes(",") ? (
                  <>
                    <span className="greeting">{displayHeading.title.split(",")[0]},</span>
                    <span> {displayHeading.title.split(",")[1]}</span>
                  </>
                ) : (
                  <span>{displayHeading.title}</span>
                )}
              </div>
              <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{displayHeading.sub}</div>
            </div>
            <span className="spacer"/>
            <div className="quick">
              <button className="quick-chip" onClick={() => goto("orders")}>
                <span className="qc-ico" style={{ color: "var(--cyan)" }}>{I.orders}</span>
                <span className="qc-meta">
                  <span className="ql">New order</span>
                  <span className="qs">Start an order draft</span>
                </span>
              </button>
              <button className="quick-chip" onClick={() => goto("production")}>
                <span className="qc-ico" style={{ color: "var(--pink)" }}>{I.flask}</span>
                <span className="qc-meta">
                  <span className="ql">Production</span>
                  <span className="qs">Track batches</span>
                </span>
              </button>
              <button className="quick-chip" onClick={() => goto("inventory")}>
                <span className="qc-ico" style={{ color: "var(--cream)" }}>{I.inventory}</span>
                <span className="qc-meta">
                  <span className="ql">Inventory</span>
                  <span className="qs">Check stock levels</span>
                </span>
              </button>
            </div>
            <button className="btn btn-ghost btn-icon"
                    title={theme === "dark" ? "Switch to light" : "Switch to dark"}
                    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                    aria-label="Toggle theme">
              {theme === "dark" ? I.sun : I.moon}
            </button>
          </div>
          <main className="main">
            <div key={current.id} className="view-enter">
              {current.View({ goto })}
            </div>
          </main>
        </div>
      </div>
    </ToastHost>
  );
}

/* ===== Root App with auth gate ===== */
function App() {
  const { loading, user, profile } = useAuth();

  if (loading) return <LoadingScreen />;

  if (INIT_SEARCH.includes("reset=1")    || INIT_HASH.includes("reset=1"))    return <ResetScreen />;
  if (INIT_SEARCH.includes("type=invite") || INIT_HASH.includes("type=invite")) return <WelcomeScreen />;

  if (!user) {
    return <LoginScreen />;
  }

  return <AppShell user={user} profile={profile} />;
}

export default App;
